import type { ExpandableMenuList, HomeLink } from '@pesto/ui/components/Navigation/Navigation.types';

import { useAuth } from '@pesto/features';
import { getEnvBadgeConfig } from '@pesto/ui/components/EnvBadge';
import { PestoWordmark } from '@pesto/ui/components/PestoWordmark';
import { Skeleton } from '@pesto/ui/components/Skeleton';
import { ApplicationLayout } from '@pesto/ui/layouts/ApplicationLayout';
import { HeaderContentProvider, HeaderContentRenderer } from '@pesto/ui/providers/HeaderContentProvider';
import { useTheme } from '@pesto/ui/providers/ThemeProvider';
import { humanize } from '@pesto/utils/src/string/stringHelper';
import { Star } from 'lucide-react';
import * as React from 'react';

import { useCurrentUserQuery } from '../__generated__/graphql/api';
import { RoleMimicSelector } from '../components/RoleMimicSelector';
import { routeConstant } from '../constants/routes';
import { useCurrentRole } from '../hooks/useCurrentRole';
import { useIsPublicRoute } from '../hooks/useIsPublicRoute';
import { buildSCMenuLinks } from './buildSCMenuLinks';

export interface ApplicationLayoutProps {
  className?: string;
  children?: React.ReactNode;
  component?: React.ReactNode;
}

export const SCApplicationLayout = (props: ApplicationLayoutProps) => {
  const { children } = props;
  const { signOut, isAuthenticated } = useAuth();
  const isPublicRoute = useIsPublicRoute();

  const { data } = useCurrentUserQuery({
    skip: isPublicRoute || !isAuthenticated(),
  });

  const user = data?.currentUser;
  const { setTheme, setColorScheme, availableColorSchemes } = useTheme();
  const { role: currentRole, isLoading: isRoleLoading } = useCurrentRole();

  const userMenuItems: ExpandableMenuList = [
    { type: 'label', label: user?.displayName || 'My Account' },
    { type: 'separator' },
    { type: 'item', label: 'Settings', onClick: () => console.log('Settings clicked') },
    { type: 'item', label: 'Support', onClick: () => console.log('Support clicked') },
    {
      type: 'sub',
      label: 'Theme',
      items: [
        { type: 'item', label: 'Light', onClick: () => setTheme('light') },
        { type: 'item', label: 'Dark', onClick: () => setTheme('dark') },
        { type: 'item', label: 'System', onClick: () => setTheme('system') },
      ],
    },
    {
      type: 'sub',
      label: 'Color Scheme',
      items: availableColorSchemes.map((scheme) => ({
        type: 'item',
        label: humanize(scheme),
        onClick: () => setColorScheme(scheme),
      })),
    },
    { type: 'separator' },
    { type: 'item', label: 'Logout', onClick: () => signOut() },
  ];

  const skeletonMenuLinks: ExpandableMenuList = Array.from({ length: 5 }, (_, i) => ({
    type: 'custom' as const,
    label: `skeleton-${i}`,
    component: <Skeleton className="bg-sidebar-text/10 h-8 w-full rounded-lg" />,
  }));

  const menuLinks = isRoleLoading ? skeletonMenuLinks : buildSCMenuLinks(currentRole);

  const sidebarEnvBadgeClassName =
    'border-sidebar-text/25 bg-sidebar-text-selected-foreground/10 text-sidebar-text-selected-foreground shadow-sm hover:bg-sidebar-text-selected-foreground/15';

  const envBadge =
    getEnvBadgeConfig({
      classNameByLabel: {
        Dev: sidebarEnvBadgeClassName,
        Local: sidebarEnvBadgeClassName,
        Staging: sidebarEnvBadgeClassName,
      },
    }) ?? undefined;

  const homeLink: HomeLink = {
    name: 'Starlink Control',
    href: routeConstant.home,
    icon: Star,
    Logo: PestoWordmark,
    subtitle: 'Super Control',
    envBadge,
  };

  const logo = homeLink;

  if (isPublicRoute) {
    return <>{children}</>;
  }

  return (
    <HeaderContentProvider>
      <ApplicationLayout
        desktopNavClassName="bg-sidebar"
        triggerClassName="text-background"
        userMenuItems={userMenuItems}
        menuLinks={menuLinks}
        homeLink={logo}
        toastHistory
        component={
          <div className="flex w-full items-center justify-between gap-4">
            <HeaderContentRenderer />
            <div className="w-full">
              <div className="flex items-center justify-end gap-2">
                <RoleMimicSelector />
              </div>
            </div>
          </div>
        }
      >
        {children}
      </ApplicationLayout>
    </HeaderContentProvider>
  );
};
